import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../../../stores/types/types';
import Icon, { COLORS, ICONS } from '../../../general-components/icon';
import TextInput from '../../../general-components/text-input';
import { updateProfile } from '../../../../stores/actions/session';
import { ConnectedUser } from '../../../../connection/socket';
import { GreenroomUser } from '../../../../stores/reducers/session';
import { ProfileInterface } from '../../../../stores/types/user-profiles';
import useEditProfileMask from '../../../../hooks/use-edit-profile-mask';
import { RegenerateStaleContentForMeeting } from '../../../../connection/generate-content';
import colors from '../../../../utils/colors';

type fieldName = 'first_name' | 'last_name' | 'title' | 'location';
interface EditableFields extends Partial<ConnectedUser> {
	edited?: boolean;
}

const style = {
	wrapper: {margin: '8px 0'},
	label: {fontSize: '11px', fontWeight: '600'},
	disabled: {border: 'none', backgroundColor: colors.blGrayscale800, color: colors.blGrayscale500},
	icon: {
		title: <Icon name={ICONS.BRIEFCASE} color={COLORS.TEXT_GRAY_400} size={16} />,
		location: <Icon name={ICONS.LOCATION} color={COLORS.TEXT_GRAY_400} size={16} />,
		email: <Icon name={ICONS.EMAIL_FILLED} color={COLORS.TEXT_GRAY_400} size={16} />
	}
}

interface Props {
	profile?: ConnectedUser | GreenroomUser | ProfileInterface | null;
	isEditingMask?: boolean;
}

export default function ProfileTab({profile, isEditingMask}: Props) {
	const dispatch = useDispatch();
	const localUserId = useSelector((s: GS) => s.SessionReducer.user?.gr_user);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const loadingProfile = useSelector((s: GS) => s.SessionReducer.loadingProfile);
	const room = useSelector((s: GS) => s.SessionReducer.meeting?.meeting)

	const [fields, setFields] = useState<EditableFields>({});
	
	// saveProfileMask takes a partial - doesn't need to be memoized
	const {saveProfileMask} = useEditProfileMask({watchedProfile: profile});

	function saveProfile() {
		if (!fields.edited) return; // see editField

		if (fields.gr_user && token && room) {
			if (!isEditingMask && fields.gr_user === localUserId) {
				dispatch(updateProfile(fields.gr_user, fields, token));
			} else {
				saveProfileMask(fields);
			}
			RegenerateStaleContentForMeeting(room, { userIds: [fields.gr_user] });
		} else {
			console.error('Token, gr_user, or room not found');
		}
	}

	// save on tab unmount
	useEffect(() => () => saveProfile(), []);

	// if fields are un-edited & user id different, init/reset fields
	useEffect(() => {
		if (loadingProfile || !profile?.gr_user)
			return;
		if (fields.gr_user === profile.gr_user && fields.edited)
			return;
		setFields({...profile, edited: false});
	}, [
		loadingProfile,
		fields.gr_user,
		fields.edited,
		profile?.gr_user,
		profile?.first_name,
		profile?.last_name,
		profile?.location,
		profile?.title,
	]);

	const editField = (val: string, name: fieldName) => {
		// if unequal set edited flag. Otherwise return original
		setFields((fs) => fs[name] === val ? fs : {...fs, [name]: val, edited: true});
	}

	return (<>
		<div style={{ display: 'flex', gap: '16px' }}>
			<TextInput
				placeholder={'First Name'}
				onBlur={saveProfile}
				value={fields.first_name ?? profile?.first_name ?? ''}
				onChange={(e) => editField(e.target.value, 'first_name')}
				wrapperStyle={style.wrapper}
				labelStyle={style.label}
			/>
			<TextInput
				placeholder={'Last Name'}
				onBlur={saveProfile}
				value={fields.last_name ?? profile?.last_name ?? ''}
				onChange={(e) => editField(e.target.value, 'last_name')}
				wrapperStyle={style.wrapper}
				labelStyle={style.label}
			/>
		</div>
		<TextInput
			placeholder={'Title'}
			onBlur={saveProfile}
			value={fields.title ?? profile?.title ?? ''}
			onChange={(e) => editField(e.target.value, 'title')}
			iconPosition='left'
			icon={style.icon.title}
		/>
		<TextInput
			placeholder={'Location'}
			onBlur={saveProfile}
			value={fields.location ?? profile?.location ?? ''}
			onChange={(e) => editField(e.target.value, 'location')}
			iconPosition='left'
			icon={style.icon.location}
		/>
		<TextInput
			disabled={true}
			placeholder={'Email'}
			value={profile?.email ?? ''}
			inputStyle={style.disabled}
			iconPosition='left'
			icon={style.icon.email}
		/>
	</>);
}
